import React from 'react';
import './SubmitFeedback.css';

// Activity type → what the user is saving, as worded in submit feedback.
export const SUBMIT_NOUNS = Object.freeze({ workout: 'workout', meal: 'meal', media: 'files', metric: 'measurements', survey: 'survey' });
export const IDLE_COMPLETION = Object.freeze({ pending: false, stage: null, saved: false });
const STAGE_TEXT = { checking: 'Checking requirements…', location: 'Getting location…', finishing: 'Finishing up…' };

// Presentation of a real completion lifecycle. Callers own pending/stage/saved
// and must only report saved after the completion write persisted.
export const submitFeedbackText = ({ pending = false, stage = null, saved = false } = {}, noun) => {
  if (pending) return stage === 'saving' ? `Saving ${noun}…` : STAGE_TEXT[stage] || STAGE_TEXT.checking;
  return saved ? `${noun.charAt(0).toUpperCase()}${noun.slice(1)} saved` : '';
};

// Keeps the idle label's geometry and accessible name throughout every stage.
export const SubmitFeedbackButton = ({ label, icon, feedback, noun, className = '', disabled = false, ...buttonProps }) => {
  const { pending = false, saved = false } = feedback;
  const active = pending || saved;
  return (
    <button
      type="button"
      {...buttonProps}
      className={`submit-feedback-button ${className}`.trim()}
      aria-label={label}
      disabled={disabled || active}
      aria-busy={pending}
    >
      {icon && (
        <span className="submit-feedback-icon" aria-hidden="true">
          {pending ? <span className="submit-feedback-spinner" /> : icon}
        </span>
      )}
      <span className="submit-feedback-label">
        <span aria-hidden={active} className={active ? 'submit-feedback-idle-hidden' : undefined}>{label}</span>
        <span className="submit-feedback-status" aria-hidden="true">{submitFeedbackText(feedback, noun)}</span>
      </span>
    </button>
  );
};

// Non-interactive progress for completions the user did not start manually.
export const SubmitFeedbackProgress = ({ feedback, noun, className = '' }) => (
  <div className={`submit-feedback-progress ${className}`.trim()} aria-busy={Boolean(feedback.pending)} aria-hidden="true">
    {feedback.pending && <span className="submit-feedback-icon"><span className="submit-feedback-spinner" /></span>}
    <span>{submitFeedbackText(feedback, noun)}</span>
  </div>
);

// Mounted outside every busy subtree so stage changes are announced.
export const SubmitFeedbackAnnouncement = ({ feedback, noun }) => (
  <span className="submit-feedback-announcement" role="status" aria-live="polite" aria-atomic="true">
    {submitFeedbackText(feedback, noun)}
  </span>
);
